import { AxiosError, AxiosHeaders, AxiosResponse } from 'axios';

/**
 * Production's API gateway answers some failures with a 200.
 *
 * nginx-api-gateway sends a 5xx from the BFF to its `@circuitbreaker`
 * location, which answers a 501, 503 or 504 with HTTP 200 and a body of its
 * own, `{"status":"error","code":"503","message":"Service Unavailable",...}`,
 * without the service's body and headers. axios resolves that as a success,
 * so a caller reads the gateway's body as the answer. The code is a number
 * when nginx retried, and `$upstream_status` lists every try ("503, 503"):
 * the last one is the answer. Staging goes through Traefik, which passes the
 * service's status, so this only happens in production.
 *
 * A BFF envelope is `{success, code, message, data}` and has no `status`, so
 * an answer of the service is never mistaken for it.
 */
export function gatewayErrorBodyStatus(body: unknown): number | null {
  if (typeof body !== 'object' || body === null) return null;

  const { status, code } = body as { status?: unknown; code?: unknown };
  if (status !== 'error') return null;

  const tries = String(code ?? '').match(/\d{3}/g);
  const last = tries ? Number(tries[tries.length - 1]) : NaN;

  return last >= 500 && last <= 599 ? last : null;
}

/**
 * The body, or the error the request gets where nothing rewrites it: an
 * AxiosError whose response carries the service's status, with the gateway's
 * body as its data. The service's own reason is lost on the way.
 */
export function rejectGatewayErrorBody<T>(body: T): T {
  const status = gatewayErrorBodyStatus(body);
  if (status === null) return body;

  const response: AxiosResponse = {
    status,
    statusText: '',
    data: body,
    headers: new AxiosHeaders(),
    config: { headers: new AxiosHeaders() },
  };

  // The same message and code axios gives a 5xx it receives as one.
  throw new AxiosError(
    `Request failed with status code ${status}`,
    AxiosError.ERR_BAD_RESPONSE,
    undefined,
    undefined,
    response,
  );
}
